/**
 * Moving the camera over an unbounded page.
 *
 * All pure functions of a camera and some screen coordinates, so the awkward
 * part of an infinite canvas — that zooming must keep the thing under your
 * fingers *under your fingers* — is testable without a browser. Getting that
 * wrong is what makes a pinch feel like the paper is sliding away from you.
 */

import type { Camera } from './scene.ts';
import { clampZoom, toWorld } from './scene.ts';

export function panBy(cam: Camera, dxScreen: number, dyScreen: number): Camera {
  return { ...cam, x: cam.x - dxScreen / cam.scale, y: cam.y - dyScreen / cam.scale };
}

/**
 * Zoom about a fixed screen point: the world point under (sx, sy) is the same
 * before and after.
 */
export function zoomAt(cam: Camera, factor: number, sx: number, sy: number, width: number, height: number): Camera {
  const anchor = toWorld(cam, sx, sy, width, height);
  const scale = clampZoom(cam.scale * factor);
  return {
    scale,
    x: anchor.x - (sx - width / 2) / scale,
    y: anchor.y - (sy - height / 2) / scale,
  };
}

export interface Touch {
  x: number;
  y: number;
}

/**
 * A two-finger drag: pan by the movement of the midpoint, zoom by the change
 * in the distance between the fingers, both anchored so the world point that
 * was between the fingers stays between them.
 */
export function pinch(
  cam: Camera,
  prevA: Touch,
  prevB: Touch,
  nextA: Touch,
  nextB: Touch,
  width: number,
  height: number
): Camera {
  const prevDist = Math.hypot(prevA.x - prevB.x, prevA.y - prevB.y);
  const nextDist = Math.hypot(nextA.x - nextB.x, nextA.y - nextB.y);
  const prevMid = { x: (prevA.x + prevB.x) / 2, y: (prevA.y + prevB.y) / 2 };
  const nextMid = { x: (nextA.x + nextB.x) / 2, y: (nextA.y + nextB.y) / 2 };

  // The world point that was under the old midpoint.
  const anchor = toWorld(cam, prevMid.x, prevMid.y, width, height);
  const scale = clampZoom(prevDist > 4 && nextDist > 4 ? cam.scale * (nextDist / prevDist) : cam.scale);

  return {
    scale,
    x: anchor.x - (nextMid.x - width / 2) / scale,
    y: anchor.y - (nextMid.y - height / 2) / scale,
  };
}
